import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input
} from "reactstrap";
import debounce from "lodash.debounce";
import axios from "axios";
import { getEvent } from "@/services/program/getEvent";
import AutocompleteInput from "./AutocompleteInput";
import PlusIcon from "mdi-react/PlusIcon";
import FormatListBulletedIcon from "mdi-react/FormatListBulletedIcon";
import TrashCanOutlineIcon from "mdi-react/TrashCanOutlineIcon";
import InformationOutlineIcon from "mdi-react/InformationOutlineIcon";
import ArrowRightIcon from "mdi-react/ArrowRightIcon";
import ClockOutlineIcon from "mdi-react/ClockOutlineIcon";
import PencilOutlineIcon from "mdi-react/PencilOutlineIcon";
import BankOutlineIcon from "mdi-react/BankOutlineIcon";
import CheckCircleOutlineIcon from "mdi-react/CheckCircleOutlineIcon";
import LockOutlineIcon from "mdi-react/LockOutlineIcon";
import {isBadgeAward, flashDispatch, flashMessage, normalizeRichTextMessage, isScheduledAwardEnabled, isScheduledAwardRequired}  from "@/shared/helpers";
import DatePicker from "react-datepicker";
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
// Rejection codes the API sends on a refused batch (App\Enums\SendRewardRejection); only this one changes the flow here.
const DUPLICATE_AWARD_CODE = "duplicate_award";

const getEmptyRecipient = (programName = "", defaultEvent = null) => ({
  id: null,
  email: "",
  first_name: "",
  last_name: "",
  phone: "",
  amount: defaultEvent?.max_awardable_amount || "100",
  event_id: defaultEvent?.id || "",
  event: defaultEvent || null,
  message: programName ? `From ${programName}, thank you!` : "",
  isAmountDisabled: false,
  scheduled_date: null,
});

const SendRewardModal = ({
  isOpen,
  setOpen, 
  toggle,
  participants = [],
  program,
  setRefreshUsers,
  organization,
  balance,
  refreshBalance,
  refreshApprovals
}) => {
  const [recipients, setRecipients] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [activeField, setActiveField] = useState({
  index: null,
  field: null,
});
  const [events, setEvents] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [globalAmount, setGlobalAmount] = useState("");
  const [query, setQuery] = useState("");
  const [step, setStep] = useState(1);
  const containerRef = useRef(null);
  const dispatch = flashDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [showApplyAllModal, setShowApplyAllModal] = useState(false);
  const [applyAllAmount, setApplyAllAmount] = useState("");
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);
  
  const searchUrl = `/organization/${organization.id}/program/${program.id}/user`;
  useEffect(() => {
    if (participants && participants.length > 0) {
      setRecipients(
        participants.map((p) => ({
          id: p.id,
          email: p.email || "",
          first_name: p.first_name || "",
          last_name: p.last_name || "",
          phone: p.phone || "",
          is_participant: p.isParticipant || false,
          award_level: p.award_level || null,
          amount: "",
          event_id: "",
          event: null,
          message: "",
          isAmountDisabled: false,
          scheduled_date: null,
        }))
      );
    } else {
      setRecipients([getEmptyRecipient()]);
    }
  }, [participants]);


  const searchUsers = async (query) => {
    if (query.length < 3) return;

    setLoadingSuggestions(true);

    try {
      const res = await axios.get(
        `/organization/${organization.id}/program/${program.id}/user`,
        { params: { keyword: query, page: 1, limit: 5 } }
      );

      setSuggestions(res.data?.data || []);
    } catch (e) {
      console.error("Suggestion fetch error", e);
      setSuggestions([]);
    }

    setLoadingSuggestions(false);
  };

  const debouncedSearch = useMemo(
    () => debounce(searchUsers, 300),
    [program, organization]
  );

  
  const handleChange = (index, field, value) => {
    const updated = [...recipients];

    // Check if we are trying to edit a locked field for an existing user
    if (updated[index].id && ["first_name", "last_name"].includes(field)) {
        return; 
    }
    updated[index][field] = value;
    updated[index].rejection = null;

    // reset ID if editing manually
    if (["email", "first_name", "last_name"].includes(field)) {
      updated[index].id = null;
      setActiveField({ index, field });
      debouncedSearch(value);
    }

    setRecipients(updated);
  };

  useEffect(() => {
    if (isOpen) {
        setStep(1); // Always start at the beginning
        setIsSubmitting(false); // Ensure the form isn't locked
        setAttempted(false);
        setDuplicatePrompt(null);
    }
  }, [isOpen]);

  // A duplicate prompt belongs to the exact list the API refused. Rows are only edited on step 1, so
  // leaving the review step retires the prompt; a resend after edits goes through the duplicate
  // check again instead of carrying the old "Send again" confirmation onto a different list.
  useEffect(() => {
    if (step === 1) setDuplicatePrompt(null);
  }, [step]);

  const selectSuggestion = (index, user) => {
    console.log("Selected user:", user);
    const isDuplicate = recipients.some((r, i) => i !== index && r.email === user.email);
    if (isDuplicate) {
      alert("This user is already in your recipient list.");
      return;
    }
    const updated = [...recipients];

    updated[index] = {
      ...updated[index],
      rejection: null,
      id: user.id,
      email: user.email,
      is_participant: user.isParticipant,
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone || ""
    };

    setRecipients(updated);
    setSuggestions([]);
    
    setActiveField({ index: null, field: null });
    setQuery("");
  };

  const hasDuplicates = useMemo(() => {
    const emails = recipients.map(r => r.email.toLowerCase().trim()).filter(e => e);
    return new Set(emails).size !== emails.length;
  }, [recipients]);

  const showScheduledDate = isScheduledAwardEnabled(program?.rootProgram, program);
  const scheduleRequired = isScheduledAwardRequired(program?.rootProgram, program);
  const colW = showScheduledDate ? {
    email: "14%", firstName: "8%", lastName: "8%", phone: "8%",
    event: "11%", amount: "8%", message: "12%", scheduled: "11%", status: "11%",
  } : {
    email: "17%", firstName: "10%", lastName: "10%", phone: "10%",
    event: "12%", amount: "9%", message: "15%", status: "12%",
  };

    useEffect(() => {
        const handleClickOutside = (event) => {
            // If the click is OUTSIDE the containerRef, close the dropdown
            if (containerRef.current && !containerRef.current.contains(event.target)) {
            setSuggestions([]);
            setActiveField({ index: null, field: null });
            setQuery("");
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    useEffect(() => {
        if (!program?.id || !organization?.id) return;

        const fetchOrCreateInstantEvent = async () => {
            try {
                // 1. Fetch existing events
                const res = await axios.get(`/organization/${organization.id}/program/${program.id}/event`);
                let allEvents = res.data || [];

                // 2. Search for "Instant Reward" (case-insensitive check)
                const instantEvent = allEvents.find(
                    (e) => e.name.toLowerCase() === "instant reward"
                );

                if (!instantEvent) {
                    // 3. If not found, create it
                    const newEventPayload = {
                        name: "Instant Reward",
                        max_awardable_amount: "100",
                        post_to_social_wall: true,
                        award_message_editable: true,
                        enable: 1,
                        message: `<p>From ${program.name}, thank you!</p>`,
                        event_icon_id: 48,
                        event_type_id: "1", // Standard Award
                        milestone_award_frequency: null,
                        ledger_code: null
                    };

                    const createRes = await axios.post(
                    `/organization/${organization.id}/program/${program.id}/event`,
                    newEventPayload
                    );

                    // Add the newly created event to the list
                    if (createRes.data) {
                        instantEvent = createRes.data;
                        allEvents = [...allEvents, createRes.data];
                    }
                }

                setEvents(allEvents);
            } catch (error) {
                console.error("Error handling events:", error);
                // Fallback: at least set what we have if the create fails
                if(error.response?.data) setEvents(error.response.data);
            }
        };

        fetchOrCreateInstantEvent();

    }, [program.id, organization.id]);
  const handleEventChange = async (index, eventId) => {
    if(!eventId) {
      const updated = [...recipients];
      updated[index].event_id = "";
      updated[index].event = null;
      updated[index].amount = "";
      updated[index].isAmountDisabled = false;
      setRecipients(updated);
      return;
    }
    const event = events.find((e) => e.id == eventId);
    const updated = [...recipients];
    updated[index].event_id = eventId;
    updated[index].event = event;
    updated[index].message = normalizeRichTextMessage(event?.message) || "";

    if (isBadgeAward(event.event_type_id)) {
      updated[index].amount = "";
      updated[index].isAmountDisabled = true;
      setRecipients(updated);
    } else {
      updated[index].isAmountDisabled = false;
      // Подтягиваем детали event чтобы получить eventAwardsLevel
      // и выставляем amount по award_level участника (если program.use_award_levels)
      try {
        const awardLevelId = updated[index].award_level;
        if (program?.use_award_levels && awardLevelId) {
          const eventDetails = await getEvent(organization.id, program.id, eventId);
          const matchingLevel = (eventDetails?.eventAwardsLevel || []).find((level) =>
            String(level.award_level_id) === String(awardLevelId) ||
            String(level.id) === String(awardLevelId) ||
            level.name === awardLevelId
          );
          updated[index].amount = matchingLevel?.amount
            ? String(matchingLevel.amount)
            : (event?.max_awardable_amount || "100");
        } else {
          updated[index].amount = event?.max_awardable_amount || "100";
        }
      } catch {
        updated[index].amount = event?.max_awardable_amount || "100";
      }
      setRecipients([...updated]);
    }
  };

  
  const addRecipient = () => {
    setRecipients((prev) => [...prev, getEmptyRecipient(program.name)]);
  };

  const removeRecipient = (index) => {
    const updated = recipients.filter((_, i) => i !== index);
    setRecipients(updated.length ? updated : [getEmptyRecipient()]);
  };

  
  const applyAmountToAll = (value) => {
    setRecipients((prev) =>
      prev.map((r) =>
        r.isAmountDisabled ? r : { ...r, amount: value }
      )
    );
  };

  
  const getStatus = (r, index) => {
    if (r.rejection) {
        return { label: "Rejected", className: "status-error", type: "rejected", info: r.rejection };
    }
    if (!r.email || !r.event_id || (!r.isAmountDisabled && !r.amount) || (scheduleRequired && !r.scheduled_date)) {
        return { label: "Not ready", className: "status-not-ready", type: "not-ready", info: 'Missing required info' };
    }
    const currentEmail = r.email.toLowerCase().trim();
  
    const isDup = recipients.some((other, i) => {
        const otherEmail = other.email?.toLowerCase().trim();
        return i !== index && otherEmail === currentEmail && currentEmail !== "";
    });

    if (isDup) {
        return {  label: "Duplicate",  className: "status-error",  info: "Email used twice" };
    }

    if (r.id) {
        return { label: "Existing", className: "status-existing", type: "existing", info: 'In Program' };
    }

    return { label: "New", className: "status-new", type: "new", info: 'Will be created in Program' };
 };

  
  const totalAmount = recipients.reduce(
    (sum, r) => sum + (parseFloat(r.amount) || 0),
    0
  );

  
  const handleSendReview = () => {
    setAttempted(true);

    if (scheduleRequired && recipients.some(r => r.email && !r.scheduled_date)) {
      alert("Scheduled Date is required");
      return;
    }

    const valid = recipients.filter((r) => r.email && r.event_id && (r.isAmountDisabled || r.amount) && (!scheduleRequired || r.scheduled_date));

    if (!valid.length) {
      alert("Please add at least one valid recipient");
      return;
    }

    setStep(2);
  };

    const markRejectedRow = (rowNumber, reason) => {
        setRecipients((prev) => prev.map((r, i) => (i === rowNumber - 1 ? { ...r, rejection: reason } : r)));
    };

    // The API answers a batch it refused with 422 + awards_sent: 0. The app-wide axios interceptor
    // (App.jsx) resolves 4xx responses as { data, status, handled: true } after flashing data.message,
    // so the rejection arrives here as a resolved response, not a thrown error.
    const batchRejectionIn = (response) =>
        response?.status === 422 && response?.data?.awards_sent === 0 ? response.data : null;

    const handleRejectedBatch = (rejection, alreadyFlashed) => {
        if (rejection.code === DUPLICATE_AWARD_CODE) {
            setDuplicatePrompt(rejection);
            return;
        }
        markRejectedRow(rejection.row, rejection.reason);
        setStep(1);
        if (!alreadyFlashed) {
            dispatch(flashMessage(rejection.message, 'alert-danger', 'top'));
        }
    };

    const handleSubmit = async (confirmDuplicates = false) => {
        if (scheduleRequired && recipients.some(r => !r.scheduled_date)) {
            alert("Scheduled Date is required");
            return;
        }

        if (hasDuplicates || recipients.some(r => !r.email || !r.event_id || (!r.isAmountDisabled && !r.amount))) {
            alert("Please fix the errors in the form before submitting.");
            return;
        }

        setIsSubmitting(true);
        setDuplicatePrompt(null);

        // 2. Prepare the payload
        const payload = {
            ...(confirmDuplicates && { confirm_duplicates: true }),
            rewards: recipients.map(r => ({
            user_id: r.id,
            email: r.email.trim(),
            first_name: r.first_name,
            last_name: r.last_name,
            phone: r.phone,
            event_id: parseInt(r.event_id),
            amount: r.isAmountDisabled ? 0 : parseFloat(r.amount),
            message: r.message,
            is_amount_disabled: r.isAmountDisabled,
            is_participant: r.is_participant || false,
            ...(showScheduledDate && r.scheduled_date && {
              scheduled_date: [
                r.scheduled_date.getFullYear(),
                String(r.scheduled_date.getMonth() + 1).padStart(2, '0'),
                String(r.scheduled_date.getDate()).padStart(2, '0'),
              ].join('-'),
            }),
            }))
        };

        try {
            // 3. The API Call
            const response = await axios.post(
            `/organization/${organization.id}/program/${program.id}/rewards`,
            payload
            );

            const rejection = batchRejectionIn(response);
            if (rejection) {
                handleRejectedBatch(rejection, Boolean(response.handled));
                return;
            }
            if (response.handled || response.status >= 400) {
                return; // the interceptor already flashed the API's message
            }
            if (response.status === 200 || response.status === 201) {
                // 4. Success handling
                dispatch(flashMessage('Participants rewarded successfully!', 'alert-success', 'top'))
                setRefreshUsers(true);
                // Refresh the program balance
                if (refreshBalance) await refreshBalance();
                if (refreshApprovals) refreshApprovals();
                toggle(); // Close modal
                //onSubmit(response.data); // Notify parent component of success
            }
        } catch (error) {
            console.error("Submission error:", error);
            const rejection = batchRejectionIn(error.response);
            if (rejection) {
                handleRejectedBatch(rejection, false);
                return;
            }
            const errorMsg = error.response?.data?.message || "Failed to send rewards. Please try again.";
            dispatch(flashMessage(errorMsg, 'alert-danger', 'top'));
        } finally {
            setIsSubmitting(false);
        }
    };
  
  return (
    <Modal isOpen={isOpen}
    toggle={() => setOpen(true)} size="xl" className="send-reward-modal" style={{ maxWidth: "95vw", width: "95vw" }}>
    {step === 1 && (
    <ModalHeader toggle={toggle} className="border-0 send-reward-header p-0">
        <div className="modal-title-custom">
        Send Reward
        </div>
        <small className="subtitle">
            Reward participants, employees, vendors, or anyone with awards they can redeem later.
        </small>
    </ModalHeader>
    )}
    {step === 2 && (
        <ModalHeader toggle={toggle} className="border-0 send-reward-header review-header p-0">
            <div className="modal-title-custom">
            Review & Send
            </div>
            <small className="subtitle">
            Please review the details below before sending this reward.
            </small>
        </ModalHeader>
    )}

  <ModalBody className={`${step === 1 ? 'send-reward-body' : 'review-send-body'} ${isSubmitting ? 'processing-mode' : ''}`}>

    {step === 1 && (
        <>
        <div className="section">
        <div className="section-header d-flex justify-content-between align-items-center">
            <div>
                <h5>1. Recipients</h5>
                <small className="recipient-subtitle">
                    Add one or more people to reward. Set the amount, event, and message for each recipient.
                </small>
            </div>

            <button
                className="apply-all-btn apply-all-link"
                onClick={() => setShowApplyAllModal(true)}
                >
                <FormatListBulletedIcon size={16} /> Apply amount to all
            </button>
            </div>
            {/* <div className="apply-all-bar">
            <Input
                type="number"
                placeholder="Enter amount"
                value={globalAmount}
                onChange={(e) => setGlobalAmount(e.target.value)}
            />
            </div> */}
        {/* TABLE HEADER */}
        <div className="recipient-header row">
            <div className="col" style={{ flex: `0 0 ${colW.email}` }}>Email *</div>
            <div className="col" style={{ flex: `0 0 ${colW.firstName}` }}>First Name *</div>
            <div className="col" style={{ flex: `0 0 ${colW.lastName}` }}>Last Name *</div>
            <div className="col" style={{ flex: `0 0 ${colW.phone}` }}>Phone (Optional)</div>
            <div className="col" style={{ flex: `0 0 ${colW.event}` }}>Award Event *</div>
            <div className="col" style={{ flex: `0 0 ${colW.amount}` }}>Amount *</div>
            <div className="col" style={{ flex: `0 0 ${colW.message}` }}>Message (Editable)</div>
            {showScheduledDate && <div className="col" style={{ flex: `0 0 ${colW.scheduled}` }}>Scheduled Date{scheduleRequired && " *"}</div>}
            <div className="col" style={{ flex: `0 0 ${colW.status}` }}>Status
                <Tooltip
                title="Not ready: Missing required info. Existing: Recipient already in program. New: Recipient will be added to program. Duplicate: Email used twice in list."
                arrow
                placement="right"
                >
                <IconButton size="small" >
                    <InformationOutlineIcon size={18} className="info-icon-svg" />
                </IconButton>
                </Tooltip>
            </div>
            <div className="col-auto"></div>
        </div>

        {/* ROWS */}
        {recipients.map((r, index) => {
            const status = getStatus(r, index);

            return (
            <div key={index} className="recipient-row row align-items-center">
                <div className="col" style={{ flex: `0 0 ${colW.email}` }} data-label="Email *">
                    <AutocompleteInput
                        value={r.email}
                        field="email"
                        searchUrl={searchUrl}
                        onChange={(val) => handleChange(index, "email", val)}
                        onSelect={(user) => selectSuggestion(index, user)}
                        placeholder="Email"
                    />
                </div>
                <div className="col" style={{ flex: `0 0 ${colW.firstName}` }} data-label="First Name *">
                    <AutocompleteInput
                        value={r.first_name}
                        field="first_name"
                        searchUrl={searchUrl}
                        onChange={(val) => handleChange(index, "first_name", val)}
                        readOnly={!!r.id}
                        onSelect={(user) => selectSuggestion(index, user)}
                        className={r.id ? "bg-light" : ""}
                        placeholder="First Name"
                    />
                </div>
                <div className="col" style={{ flex: `0 0 ${colW.lastName}` }} data-label="Last Name *">
                    <AutocompleteInput
                        value={r.last_name}
                        field="last_name"
                        searchUrl={searchUrl}
                        onChange={(val) => handleChange(index, "last_name", val)}
                        readOnly={!!r.id}
                        onSelect={(user) => selectSuggestion(index, user)}
                        className={r.id ? "bg-light" : ""}
                        placeholder="Last Name"
                    />
                </div>
                <div className="col" style={{ flex: `0 0 ${colW.phone}` }} data-label="Phone (Optional)">
                    <Input value={r.phone} onChange={(e) => handleChange(index, "phone", e.target.value)} placeholder="Phone" />
                </div>
                <div className="col" style={{ flex: `0 0 ${colW.event}` }} data-label="Award Event *">
                    <Input
                        type="select"
                        value={r.event_id}
                        onChange={(e) => handleEventChange(index, e.target.value)}
                    >
                        <option value="">Select Event</option>
                        {events.map((ev) => (
                        <option key={ev.id} value={ev.id}>
                            {ev.name}
                        </option>
                        ))}
                    </Input>
                </div>
                <div className="col" style={{ flex: `0 0 ${colW.amount}` }} data-label="Amount *">
                   <div 
                        className="amount-input-wrapper"
                        style={{ display: "flex", alignItems: "stretch", height: "36px" }}
                    >
                        <span style={{ 
                            display: "flex",
                            alignItems: "center",
                            padding: "0 8px",
                            background: "#f5f5f5", 
                            border: "1px solid #ced4da", 
                            borderRight: "none",
                            borderRadius: "4px 0 0 4px",
                            fontSize: "13px",       /* ← match the SCSS font-size */
                            color: "#495057",
                            whiteSpace: "nowrap",
                            lineHeight: 1,
                            boxSizing: "border-box"
                        }}>$</span>
                        <Input
                            type="number"
                            disabled={r.isAmountDisabled}
                            value={r.isAmountDisabled ? "N/A" : r.amount}
                            onChange={(e) => handleChange(index, "amount", e.target.value)}
                            style={{ 
                                borderRadius: "0 4px 4px 0", 
                                flex: 1, 
                                minWidth: 0,
                                boxSizing: "border-box"
                                /* no height here — let SCSS rule own it */
                            }}
                            placeholder="0.00"
                        />
                    </div>
                </div>

                <div className="col" style={{ flex: `0 0 ${colW.message}` }} data-label="Message (Optional)">
                    <Input type="textarea" placeholder="Add a message" rows={2} value={r.message} onChange={(e) => handleChange(index, "message", e.target.value)} />
                </div>

                {showScheduledDate && (
                  <div className="col" style={{ flex: `0 0 ${colW.scheduled}` }} data-label={scheduleRequired ? "Scheduled Date *" : "Scheduled Date"}>
                    <DatePicker
                      selected={r.scheduled_date}
                      onChange={(date) => handleChange(index, "scheduled_date", date)}
                      minDate={new Date()}
                      dateFormat="MM/dd/yyyy"
                      placeholderText="Select date"
                      className={`form-control form-control-sm${attempted && scheduleRequired && !r.scheduled_date ? " is-invalid" : ""}`}
                      popperClassName="send-reward-modal__datepicker-popper"
                      popperProps={{ strategy: "fixed" }}
                    />
                  </div>
                )}

                <div className="col" style={{ flex: `0 0 ${colW.status}` }} data-label="Status">
                    <div className="status-wrapper">
                        <span className={`status-badge ${status.className}`}>
                        {status.type === "not-ready" && <ClockOutlineIcon size={14} />}
                        <span className="status-label-text">{status.label}</span>
                        </span>
                        <small 
                        className="status-info-text d-none d-lg-block" 
                        style={{ fontSize: "13px", display: "block", marginTop: "2px" }}
                        >
                        {status.info}
                        </small>
                    </div>
                </div>

                <div className="col-auto">
                    <button
                        className="remove-btn"
                        onClick={() => removeRecipient(index)}
                    >
                        <TrashCanOutlineIcon size={16} />
                    </button>
                </div>
            </div>
            );
        })}

        <div className="recipient-bottom-bar">
            <div
                className="add-recipient-link"
                onClick={addRecipient}
            >
                <PlusIcon size={16} />
                Add another recipient
            </div>

            <div
                className="apply-all-link border-0 p-0"
                onClick={() => setShowApplyAllModal(true)}
                >
                <FormatListBulletedIcon size={16} />
                Apply amount to all
            </div>
        </div>
        </div>

        {/* FOOTER SUMMARY */}
        <div className="summary-bar">
            <div>Total Recipients: <span className="summary-total-value">{recipients.length}</span></div>
            <div>Total Award: <span className="summary-award-value">${totalAmount.toFixed(2)}</span></div>
        </div>
        </>
    )}
    {step === 2 && (
        <div className="review-container">
            {duplicatePrompt && (
                <div className="duplicate-prompt alert alert-warning" role="alert">
                    <p className="mb-2">{duplicatePrompt.message}</p>
                    <ul className="duplicate-list mb-2">
                        {duplicatePrompt.duplicates.map((d) => (
                            <li key={`${d.row}-${d.email}`}>
                                <span>{d.email}</span> — ${Number(d.amount).toFixed(2)} at {new Date(d.awarded_at).toLocaleTimeString("en-US")}
                            </li>
                        ))}
                    </ul>
                    <div className="d-flex" style={{ gap: "8px" }}>
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setDuplicatePrompt(null)}>
                            Keep editing
                        </button>
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => handleSubmit(true)} disabled={isSubmitting}>
                            Send again
                        </button>
                    </div>
                </div>
            )}
            <div className="row">
                {/* LEFT COLUMN */}
                <div className="col-xl-8">
                    {/* 1. RECIPIENTS CARD */}
                    <div className="review-card">
                        <div className="card-header-custom">
                            <span className="step-title">1. Recipients ({recipients.length})</span>
                            <button className="btn-edit-outline" onClick={() => setStep(1)}>
                                <PencilOutlineIcon size={14} /> Edit
                            </button>
                        </div>

                        <div className="review-table">
                            <div className="review-table-header">
                                <div className="rt-col col-user">Recipient</div>
                                <div className="rt-col col-phone">Phone</div>
                                <div className="rt-col col-amount">Value (USD)</div>
                                <div className="rt-col col-event">Award Event</div>
                                <div className="rt-col col-message">Message</div>
                                {showScheduledDate && <div className="rt-col col-scheduled-date">Scheduled Date</div>}
                            </div>

                            {recipients.map((r, i) => (
                                <div key={i} className="review-table-row">
                                    <div className="rt-col col-user">
                                        <div className="rr-avatar">
                                        {r.first_name?.charAt(0)}{r.last_name?.charAt(0)}
                                        </div>
                                        <div className="rr-user-info">
                                        <div className="rr-name">{r.first_name} {r.last_name}</div>
                                        <div className="rr-email">{r.email}</div>
                                        </div>
                                    </div>
                                    <div className="rt-col col-phone">{r.phone || "-"}</div>
                                    <div className="rt-col col-amount">
                                        <div className="rr-amount">${parseFloat(r.amount || 0).toFixed(2)}</div>
                                        {r.event?.max_value && (
                                        <div className="rr-sub">Up to ${r.event.max_value}</div>
                                        )}
                                    </div>
                                    <div className="rt-col col-event">{r.event?.name || "-"}</div>
                                    <div className="rt-col col-message">{r.message || "-"}</div>
                                    {showScheduledDate && (
                                        <div className="rt-col col-scheduled-date">
                                            {r.scheduled_date ? r.scheduled_date.toLocaleDateString("en-US") : "-"}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="add-another-footer" onClick={() => setStep(1)}>
                            <span className="plus-icon">+</span> Add another recipient
                        </div>
                    </div>

                </div>

                {/* RIGHT COLUMN */}
                <div className="col-xl-4">
                    <div className="reward-summary-card">
                        <h5 className="summary-title">Reward Summary</h5>
                        <div className="summary-list">
                            <div className="summary-row">
                                <span className="summary-label">Total Recipients</span>
                                <span className="summary-value">{recipients.length}</span>
                            </div>

                            <div className="summary-row">
                                <span className="summary-label">Total Award Amount</span>
                                <span className="summary-value highlight">${totalAmount.toFixed(2)} USD</span>
                            </div>

                            <div className="summary-row">
                                <span className="summary-label">Payment Method</span>
                                <span className="summary-value highlight">Program Balance</span>
                            </div>
                        </div>
                        {/* Logic: Only show the success box if totalAmount > 0 and balance is enough */}
                        {totalAmount > 0 && balance >= totalAmount ? (
                            <div className="balance-status-box success">
                                <div className="status-header">
                                <CheckCircleOutlineIcon size={20} />
                                <span>Sufficient Balance</span>
                                </div>
                                <p>Your program balance is sufficient to cover this reward.</p>
                            </div>
                        ) : totalAmount > 0 && balance < totalAmount && !program?.invoice_for_awards ? (
                        /* Optional: Show an error state if balance is too low */
                        <div className="balance-status-box error">
                            <div className="status-header">
                                <Tooltip
                                    title="Your program balance is too low to cover the total reward amount. Please add funds to your program or reduce the reward amount."
                                    arrow
                                    placement="right"
                                    >
                                        <IconButton size="small" >
                                            <InformationOutlineIcon size={20} />
                                        </IconButton>
                                </Tooltip>
                                <span>Insufficient Balance</span>
                            </div>
                            <p>You need ${(totalAmount - balance).toFixed(2)} more to send this reward.</p>
                        </div>
                        ) : null}

                        <div className="info-box">
                            <div className="info-icon-wrap">
                                <Tooltip
                                title="Rewards are typically delivered within minutes. Recipients will receive an email notification."
                                arrow
                                placement="right"
                                >
                                    <IconButton size="small" >
                                        <InformationOutlineIcon size={20} color="#7f8799"/>
                                    </IconButton>
                                </Tooltip>
                            </div>
                            <p className="w-100">Rewards are typically delivered within minutes. Recipients will receive an email notification.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )}
  </ModalBody>

  <ModalFooter className="modal-footer-custom">
    {step === 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            
            <button 
                className="btn-cancel" 
                onClick={toggle}
                style={{
                    height: "44px",
                    minWidth: "100px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}
            >
                Cancel
            </button>

            <div className="disclaimer d-flex align-items-center justify-content-between">
                <div className="total-award-container mr-3">
                    <div className="total-label">
                        Total Award ({recipients.length} {recipients.length === 1 ? "recipient" : "recipients"})
                    </div>
                    <div className="total-value-row d-flex align-items-center">
                        <div className="total-value mr-3">
                            ${totalAmount.toFixed(2)} USD
                        </div>
                        <Tooltip
                            title="This is the total reward amount for all recipients combined."
                            arrow
                            placement="right"
                        >
                            <IconButton size="small">
                                <InformationOutlineIcon size={18} className="info-icon-svg" />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>

                <button 
                    className="btn-review-send btn-primary" 
                    disabled={hasDuplicates || recipients.some(r => !r.email || !r.event_id || (!r.isAmountDisabled && !r.amount))} 
                    onClick={handleSendReview}
                    style={{
                        height: "44px",
                        minWidth: "100px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px"
                    }}
                >
                    <span>Review & Send</span>
                    <ArrowRightIcon size={18} />
                </button>
            </div>
        </div>
    )}

    {step === 2 && (
        <div className="modal-footer-review">
            {/* Left: Back Button */}
            <button className="btn-back-outline" onClick={() => setStep(1)}>
                Back
            </button>

            {/* Right: Summary and Submit */}
            <div className="d-flex align-items-center justify-content-between">
                <div className="total-summary-final mr-3 text-right">
                    <div className="total-label-final">
                        Total Award ({recipients.length} {recipients.length === 1 ? 'recipient' : 'recipients'})
                    </div>
                    <div className="total-value-final">
                        ${totalAmount.toFixed(2)} USD
                    </div>
                </div>

                <div className="submit-container">
                    <button
                    className="btn-send-reward-final"
                    disabled={isSubmitting || hasDuplicates}
                    onClick={() => handleSubmit()}
                    >
                        {isSubmitting ? (
                            <>
                            <span className="spinner-border spinner-border-sm mr-3" role="status" aria-hidden="true" />
                            <span>Processing...</span>
                            </>
                        ) : (
                            <>
                            <span className="mr-3">Send Reward</span>
                            <svg 
                                viewBox="0 0 24 24" 
                                width="20" 
                                height="20" 
                                className="ml-2" 
                                style={{ fill: 'none', stroke: 'white', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' }}
                            >
                                <path d="M22 2L11 13M22 2L15 22L11 13M11 13L2 9L22 2Z" />
                            </svg>
                        </>
                        )}
                    </button>
                    
                    <div className="undone-disclaimer">
                        <LockOutlineIcon size={18} className="mr-1" />
                        This action cannot be undone.
                    </div>
                </div>
            </div>
        </div>
    )}
  </ModalFooter>
    <Modal 
        isOpen={showApplyAllModal} 
        toggle={() => setShowApplyAllModal(false)} 
        size="md" 
        centered
        contentClassName="h-auto"
        modalClassName="apply-all-modal"
        style={{ margin: "1rem" }}
    >
        <ModalHeader toggle={() => setShowApplyAllModal(false)}>
            Apply Amount to All
        </ModalHeader>
        <ModalBody style={{ paddingBottom: "8px", flex: "0 0 auto" }}>
            <p style={{ fontSize: "14px", color: "#555", marginBottom: "12px" }}>
                Enter an amount to apply to all recipients. This will override individual amounts.
            </p>
            <div style={{ 
                display: "flex", 
                alignItems: "stretch",   // ← was default/broken
                height: "38px"           // ← explicit shared height
            }}>
                <span style={{
                display: "flex",
                alignItems: "center",
                padding: "0 10px",
                background: "#f5f5f5",
                border: "1px solid #ced4da",
                borderRight: "none",
                borderRadius: "4px 0 0 4px",
                fontSize: "14px",
                color: "#495057",
                lineHeight: 1
                }}>$</span>
                <Input
                type="number"
                placeholder="Enter amount"
                value={applyAllAmount}
                onChange={(e) => setApplyAllAmount(e.target.value)}
                style={{ 
                    borderRadius: "0 4px 4px 0",
                    height: "38px",      // ← matches the wrapper
                    flex: 1
                }}
                autoFocus
                />
            </div>
        </ModalBody>
        <ModalFooter style={{ padding: "12px 16px" }}>
            <div style={{ 
                display: "flex", 
                gap: "8px", 
                width: "100%"
            }}>
                <button
                    className="btn-cancel"
                    style={{ 
                        flex: 1,
                        height: "38px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}
                    onClick={() => {
                        setShowApplyAllModal(false);
                        setApplyAllAmount("");
                    }}
                >
                    Cancel
                </button>
                <button
                    className="btn-review-send btn-primary"
                    disabled={!applyAllAmount || parseFloat(applyAllAmount) <= 0}
                    style={{ 
                        flex: 1,
                        height: "38px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "6px",
                        padding: 0
                    }}
                    onClick={() => {
                        applyAmountToAll(applyAllAmount);
                        setShowApplyAllModal(false);
                        setApplyAllAmount("");
                    }}
                >
                    Apply to All
                </button>
            </div>
        </ModalFooter>
    </Modal>
</Modal>
  );
};

export default SendRewardModal;